import { useMemo } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import { QueryProvider } from '@shared-web/api/provider';

import { HeaderManager } from './components/Header';
import EDDRoute from './routes/edd';
import FiatActivationRoute from './routes/fiat-activation';
import HomePage from './routes/index';
import { getRequestInfo } from './utils/getRequestInfo';

/**
 * Root Layout Component
 *
 * Provides the common layout structure for all pages:
 * - HeaderManager (Logo + LanguageSwitcher)
 * - Main content area (Outlet)
 *
 * @note Exported for testing purposes
 */
export function RootLayout() {
  return (
    <div className="bg-background text-foreground flex h-screen w-screen flex-col">
      <HeaderManager />
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Create router configuration with data loader support
 * Uses createBrowserRouter for data router features (useRouteLoaderData)
 *
 * @note Created lazily inside App to avoid module-level initialization issues in tests
 */
function createRouter() {
  return createBrowserRouter([
    {
      id: 'root',
      element: <RootLayout />,
      loader: () => ({ requestInfo: getRequestInfo() }),
      children: [
        { path: '/', element: <HomePage /> },
        { path: '/edd/*', element: <EDDRoute /> },
        {
          path: '/fiat/activation/:currency',
          element: <FiatActivationRoute />,
        },
      ],
    },
  ]);
}

/**
 * App Component
 *
 * Main application component with:
 * - Data router for useRouteLoaderData support
 * - Root layout with header
 * - Route definitions
 */
export default function App() {
  // Create router lazily to avoid module-level initialization issues in tests
  // useMemo ensures the router is only created once per App instance
  const router = useMemo(() => createRouter(), []);

  return (
    <QueryProvider>
      <RouterProvider router={router} />
    </QueryProvider>
  );
}
